import { labelForCefr, labelForLanguageCode } from '@devizy/shared';
import { Skeleton, cn } from '@devizy/ui';
import {
  ArrowUpRight,
  Buildings,
  FileArrowDown,
  LinkSimple,
  MapPin,
  SealCheck,
  Translate,
  UsersThree,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { LogoWordmark } from '@/components/brand/logo';
import { trpc } from '@/lib/trpc/client';

import type { CefrLevel } from '@devizy/shared';

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export const Route = createFileRoute('/agency/$slug')({
  component: AgencyPage,
});

function PublicShell({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="bg-background flex min-h-dvh flex-col">
      <header className="border-border bg-background/85 sticky top-0 z-10 border-b backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-5 lg:px-8">
          <a href="/" aria-label="Devizy" className="shrink-0">
            <LogoWordmark variant="color" className="h-5 w-auto" />
          </a>
          <Link
            to="/offers/new"
            search={{ draftId: undefined }}
            className="border-border text-foreground-soft hover:border-border-hover hover:text-foreground hover:bg-surface focus-visible:ring-ring inline-flex h-8.5 items-center gap-1.5 rounded-lg border px-3 text-[12.5px] font-[590] transition-[background-color,border-color,color] focus-visible:ring-2 focus-visible:outline-none"
          >
            Publier une offre
            <ArrowUpRight className="size-3.5" weight="bold" />
          </Link>
        </div>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="border-border border-t">
        <div className="text-muted-foreground mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-5 py-6 text-xs lg:px-8">
          <span>Profil public propulsé par Devizy.</span>
          <a href="/" className="hover:text-foreground transition-colors">
            devizy.app
          </a>
        </div>
      </footer>
    </div>
  );
}

function AgencyNotFound(): React.ReactElement {
  return (
    <PublicShell>
      <main className="mx-auto flex max-w-lg flex-col items-center px-5 py-24 text-center lg:px-8">
        <div className="bg-surface border-border text-faint-foreground grid size-14 place-items-center rounded-2xl border">
          <Buildings className="size-6" weight="regular" />
        </div>
        <p className="text-faint-foreground mt-5 font-mono text-[11px] font-semibold tracking-[0.14em]">
          404
        </p>
        <h1 className="text-foreground mt-2 text-xl font-bold tracking-tight">
          Agence introuvable
        </h1>
        <p className="text-muted-foreground mt-2 text-[13px] leading-relaxed">
          Ce profil public n&apos;existe pas, ou l&apos;agence ne l&apos;a plus rendu visible.
        </p>
      </main>
    </PublicShell>
  );
}

function AgencySkeleton(): React.ReactElement {
  return (
    <PublicShell>
      <div className="border-border bg-surface border-b">
        <div className="mx-auto flex max-w-6xl gap-6 px-5 py-14 lg:px-8">
          <Skeleton className="size-20 shrink-0 rounded-2xl" />
          <div className="min-w-0 flex-1 space-y-3">
            <Skeleton className="h-3 w-28 rounded" />
            <Skeleton className="h-8 w-64 rounded" />
            <Skeleton className="h-3 w-full max-w-xl rounded" />
            <Skeleton className="h-3 w-full max-w-md rounded" />
          </div>
        </div>
      </div>
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-10 lg:grid-cols-[minmax(0,1fr)_19rem] lg:px-8">
        <div className="space-y-6">
          <Skeleton className="h-40 w-full rounded-2xl" />
          <Skeleton className="h-52 w-full rounded-2xl" />
        </div>
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    </PublicShell>
  );
}

function Section({
  eyebrow,
  title,
  hint,
  children,
}: {
  eyebrow: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="border-border bg-card rounded-2xl border p-5 sm:p-6">
      <p className="text-faint-foreground text-[10px] font-bold tracking-[0.13em] uppercase">
        {eyebrow}
      </p>
      <h2 className="text-foreground mt-1.5 text-base font-bold tracking-tight">{title}</h2>
      {hint ? <p className="text-muted-foreground mt-1 text-xs leading-relaxed">{hint}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Chip({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'primary';
}): React.ReactElement {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-[560]',
        tone === 'primary'
          ? 'bg-primary-tint text-primary-strong'
          : 'border-border text-foreground-soft border',
      )}
    >
      {children}
    </span>
  );
}

function LanguageTag({ code, level }: { code: string; level: string }): React.ReactElement {
  return (
    <span className="border-border text-muted-foreground inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px]">
      <span className="text-foreground-soft font-mono font-bold uppercase">{code}</span>
      <span className="bg-border h-2.5 w-px" aria-hidden />
      <span className="font-mono tabular-nums">{level}</span>
    </span>
  );
}

function FactRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}): React.ReactElement {
  return (
    <li className="flex items-center gap-3 py-2.5">
      <span className="bg-surface text-muted-foreground grid size-7 shrink-0 place-items-center rounded-lg">
        {icon}
      </span>
      <span className="text-muted-foreground min-w-0 flex-1 text-xs">{label}</span>
      <span className="text-foreground text-right text-[12.5px] font-[610]">{value}</span>
    </li>
  );
}

function ResourceLink({
  href,
  icon,
  label,
  testId,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  testId?: string;
}): React.ReactElement {
  return (
    <a
      href={href}
      rel="noopener noreferrer"
      target="_blank"
      data-testid={testId}
      className="group border-border bg-surface hover:border-border-hover hover:bg-card focus-visible:ring-ring flex items-center gap-3 rounded-xl border px-3.5 py-3 transition-[background-color,border-color,box-shadow,transform] hover:-translate-y-px hover:shadow-[0_6px_18px_-14px_hsl(var(--foreground)/0.4)] focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <span className="text-muted-foreground group-hover:text-primary shrink-0 transition-colors">
        {icon}
      </span>
      <span className="text-foreground min-w-0 flex-1 truncate text-[13px] font-[560]">
        {label}
      </span>
      <ArrowUpRight
        className="text-faint-foreground group-hover:text-primary size-4 shrink-0 transition-colors"
        weight="bold"
      />
    </a>
  );
}

function AgencyPage(): React.ReactElement {
  const { slug } = Route.useParams();
  const slugValid = Boolean(slug) && SLUG_REGEX.test(slug);
  const { data: agency, isLoading } = trpc.publicAgency.bySlug.useQuery(
    { slug },
    { enabled: slugValid, retry: false },
  );

  if (!slugValid) {
    return <AgencyNotFound />;
  }
  if (isLoading) {
    return <AgencySkeleton />;
  }
  if (!agency) {
    return <AgencyNotFound />;
  }

  const teamSize = formatTeamSize(agency.teamSizeMin, agency.teamSizeMax);
  const hasExpertise = agency.skills.length > 0 || agency.domains.length > 0;
  const hasPortfolio = agency.portfolioUrls.length > 0 || agency.portfolioFiles.length > 0;
  const portfolioCount = agency.portfolioUrls.length + agency.portfolioFiles.length;

  return (
    <PublicShell>
      <header className="border-border bg-surface relative overflow-hidden border-b">
        <div aria-hidden className="bg-paper pointer-events-none absolute inset-0 opacity-70" />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 left-1/2 h-80 w-[46rem] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,hsl(var(--primary)/0.11),transparent_70%)]"
        />
        <div className="relative mx-auto max-w-6xl px-5 pt-12 pb-11 lg:px-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-7">
            {agency.logoUrl ? (
              <img
                src={agency.logoUrl}
                alt=""
                className="border-border bg-card size-20 shrink-0 rounded-2xl border object-cover shadow-[0_10px_30px_-18px_hsl(var(--foreground)/0.45)]"
              />
            ) : (
              <div
                aria-hidden
                className="bg-primary-tint text-primary-strong border-primary/15 grid size-20 shrink-0 place-items-center rounded-2xl border text-2xl font-bold shadow-[0_10px_30px_-18px_hsl(var(--foreground)/0.45)]"
              >
                {agency.name.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="text-faint-foreground text-[10px] font-bold tracking-[0.13em] uppercase">
                  Profil d&apos;agence
                </p>
                {agency.verified ? (
                  <span className="bg-primary-tint text-primary-strong inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-[620]">
                    <SealCheck className="size-3.5" weight="fill" />
                    Vérifiée
                  </span>
                ) : null}
              </div>
              <h1 className="text-foreground mt-2 text-[28px] leading-[1.1] font-bold tracking-[-0.02em] sm:text-[34px]">
                {agency.name}
              </h1>
              {agency.city ? (
                <p className="text-muted-foreground mt-2.5 flex items-center gap-1.5 text-[13px]">
                  <MapPin className="size-4 shrink-0" weight="regular" />
                  {agency.city}
                </p>
              ) : null}
              {agency.description ? (
                <p className="text-foreground-soft mt-4 max-w-2xl text-[14px] leading-[1.65] whitespace-pre-line">
                  {agency.description}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-5 py-9 lg:grid-cols-[minmax(0,1fr)_19rem] lg:gap-8 lg:px-8 lg:py-11">
        <div className="min-w-0 space-y-6">
          {hasExpertise ? (
            <Section eyebrow="Savoir-faire" title="Expertises">
              <div className="space-y-4">
                {agency.skills.length > 0 ? (
                  <div>
                    <p className="text-faint-foreground mb-2 text-[11px] font-[590]">Compétences</p>
                    <div className="flex flex-wrap gap-1.5">
                      {agency.skills.map((s) => (
                        <Chip key={s} tone="primary">
                          {s}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ) : null}
                {agency.domains.length > 0 ? (
                  <div>
                    <p className="text-faint-foreground mb-2 text-[11px] font-[590]">Domaines</p>
                    <div className="flex flex-wrap gap-1.5">
                      {agency.domains.map((d) => (
                        <Chip key={d}>{d}</Chip>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </Section>
          ) : null}

          <Section
            eyebrow="Capacité"
            title="Équipe"
            hint="Composition déclarée par l'agence, sans identification individuelle."
          >
            <p className="text-foreground text-[13px] font-[610]">{teamSize}</p>
            {agency.teamComposition.length > 0 ? (
              <ul className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="agency-team-composition">
                {agency.teamComposition.map((entry, index) => (
                  <li
                    key={`${entry.role}-${entry.languageCode}-${String(index)}`}
                    className="border-border/70 bg-surface flex items-center gap-3 rounded-xl border px-3 py-2.5"
                  >
                    <span className="bg-primary-tint text-primary-strong grid size-8 shrink-0 place-items-center rounded-lg font-mono text-[13px] font-bold tabular-nums">
                      {entry.headcount}
                    </span>
                    <span className="text-foreground min-w-0 flex-1 truncate text-[13px] font-[560]">
                      {entry.role}
                    </span>
                    <LanguageTag code={entry.languageCode} level={entry.level} />
                  </li>
                ))}
              </ul>
            ) : null}
            {agency.teamLanguageComposition ? (
              <p className="text-muted-foreground mt-3 text-[13px] leading-relaxed whitespace-pre-line">
                {agency.teamLanguageComposition}
              </p>
            ) : null}
          </Section>

          {agency.languages.length > 0 ? (
            <Section
              eyebrow="CECRL"
              title="Langues de travail"
              hint="Niveaux agrégés au niveau de l'agence — le plus haut niveau atteint par l'équipe affectée."
            >
              <ul className="grid gap-2 sm:grid-cols-2">
                {agency.languages.map((lang) => (
                  <li
                    key={lang.code}
                    className="border-border/70 bg-surface flex items-center justify-between gap-3 rounded-xl border px-3.5 py-2.5"
                  >
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="text-primary-strong bg-primary-tint rounded-md px-1.5 py-0.5 font-mono text-[11px] font-bold uppercase">
                        {lang.code}
                      </span>
                      <span className="text-foreground truncate text-[13px] font-[560]">
                        {labelForLanguageCode(lang.code)}
                      </span>
                    </span>
                    <span className="text-muted-foreground shrink-0 text-right text-[11.5px]">
                      <span className="text-foreground font-mono font-bold">{lang.level}</span>
                      {' · '}
                      {labelForCefr(lang.level as CefrLevel)}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {hasPortfolio ? (
            <Section eyebrow="Réalisations" title="Portfolio">
              <div className="grid gap-2">
                {agency.portfolioUrls.map((url) => (
                  <ResourceLink
                    key={url}
                    href={url}
                    label={url}
                    icon={<LinkSimple className="size-4" weight="regular" />}
                  />
                ))}
                {agency.portfolioFiles.map((file) => (
                  <ResourceLink
                    key={file.url}
                    href={file.url}
                    label={file.name}
                    testId="agency-portfolio-file"
                    icon={<FileArrowDown className="size-4" weight="regular" />}
                  />
                ))}
              </div>
            </Section>
          ) : null}

          {agency.references.length > 0 ? (
            <Section eyebrow="Confiance" title="Références clients">
              <ul className="grid gap-2.5">
                {agency.references.map((ref) => (
                  <li
                    key={ref.client}
                    className="border-border/70 bg-surface relative rounded-xl border p-4 pl-5"
                  >
                    <span
                      aria-hidden
                      className="bg-primary/25 absolute top-4 bottom-4 left-0 w-0.5 rounded-full"
                    />
                    <p className="text-foreground text-[13px] font-[620]">{ref.client}</p>
                    {ref.summary ? (
                      <p className="text-muted-foreground mt-1 text-[13px] leading-relaxed">
                        {ref.summary}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>

        <aside className="lg:sticky lg:top-20 lg:h-fit">
          <div className="space-y-4">
            <div className="border-border bg-card rounded-2xl border p-5">
              <p className="text-faint-foreground text-[10px] font-bold tracking-[0.13em] uppercase">
                En bref
              </p>
              <ul className="divide-border/70 mt-2 divide-y">
                <FactRow
                  icon={<UsersThree className="size-4" weight="regular" />}
                  label="Équipe"
                  value={teamSize}
                />
                {agency.languages.length > 0 ? (
                  <FactRow
                    icon={<Translate className="size-4" weight="regular" />}
                    label="Langues"
                    value={
                      <span className="font-mono tabular-nums">{agency.languages.length}</span>
                    }
                  />
                ) : null}
                {agency.skills.length > 0 ? (
                  <FactRow
                    icon={<Buildings className="size-4" weight="regular" />}
                    label="Compétences"
                    value={<span className="font-mono tabular-nums">{agency.skills.length}</span>}
                  />
                ) : null}
                {portfolioCount > 0 ? (
                  <FactRow
                    icon={<LinkSimple className="size-4" weight="regular" />}
                    label="Portfolio"
                    value={<span className="font-mono tabular-nums">{portfolioCount}</span>}
                  />
                ) : null}
                <FactRow
                  icon={<SealCheck className="size-4" weight="regular" />}
                  label="Identité légale"
                  value={
                    agency.verified ? (
                      <span className="text-primary-strong">Vérifiée</span>
                    ) : (
                      <span className="text-muted-foreground">En cours</span>
                    )
                  }
                />
              </ul>
            </div>

            <div className="border-primary/15 bg-primary-tint/60 rounded-2xl border p-5">
              <h2 className="text-foreground text-[13.5px] font-bold tracking-tight">
                Un projet pour {agency.name} ?
              </h2>
              <p className="text-foreground-soft mt-1.5 text-[12.5px] leading-relaxed">
                Publiez votre besoin sur Devizy : l&apos;IA structure votre cahier des charges et
                vous recevez des devis comparables ligne à ligne.
              </p>
              <Link
                to="/offers/new"
                search={{ draftId: undefined }}
                className="bg-primary-deep text-primary-foreground hover:bg-primary-hover focus-visible:ring-ring mt-4 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-lg text-[12.5px] font-[620] transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                Publier une offre
                <ArrowUpRight className="size-3.5" weight="bold" />
              </Link>
            </div>
          </div>
        </aside>
      </main>
    </PublicShell>
  );
}

function formatTeamSize(min: number | null, max: number | null): string {
  if (min !== null && max !== null) {
    return min === max ? `${min} personne${min > 1 ? 's' : ''}` : `${min}–${max} personnes`;
  }
  if (min !== null) return `${min}+ personnes`;
  if (max !== null) return `Jusqu'à ${max} personnes`;
  return 'Non communiquée';
}
